import { Fragment, ReactNode, useEffect, useState } from "react";

import { Menu, Transition } from "@headlessui/react";
import {
  ArrowTopRightOnSquareIcon,
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  TruckIcon,
  ViewColumnsIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";
import { DateTime, DateTimeFormatOptions } from "luxon";
import { useTranslation } from "react-i18next";

import { IInboxConversation, IInboxUser, IOrderAction } from "../../models/messagesInbox/IInboxConversation";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import { INBOX_DAY, INBOX_DAY_TIME } from "../../utils/inboxDates";
import PriceFormat from "../../utils/priceFormat";
import { addressChangeAdvice, amendmentAdvice, cancellationAdvice, daysPastEstimatedDelivery, IOrderChangeAdvice, isLateDelivery, orderStage, wheresMyOrderReply } from "../../utils/orderStage";
import { donorHref, inboxLinks, partHref } from "../../utils/inboxLinks";
import { orderActionBlock } from "../../utils/inboxOrderActions";
import { partKeyCode } from "../../utils/partKeyCode";
import { trackingLink } from "../../utils/trackingLink";
import ClickToCopy from "../shared/ClickToCopy";
import FitmentCard from "./FitmentCard";
import { EbayCaseCard, OrderActionPanel, WarehouseAlertPanel } from "./OrderActions";
import { Chip, FieldRow, IInboxNotice, InboxError, InboxLoading, InboxNotice, PanelCard, PlateChip, PreviewNote, ResultIcon, useInboxLive } from "./InboxBits";

export type PanelTab = "details" | "notes" | "return";

function when(iso: string | undefined, format: DateTimeFormatOptions = INBOX_DAY_TIME) {
  return formatDateTime(iso, format);
}

const stockStatusTone = { "In stock": "green", Sold: "grey", "On the car": "accent", Damaged: "red" } as const;

function stockStatusLabel(status: keyof typeof stockStatusTone, t: (key: string) => string) {
  if (status === "In stock") return t("In stock");
  if (status === "Sold") return t("Sold");
  if (status === "On the car") return t("On the car");
  return t("Damaged");
}

function PartCard({ c }: { c: IInboxConversation }) {
  const { t } = useTranslation("messages");
  const p = c.part;
  if (!p) return null;
  const key = partKeyCode(p.categoryName, c.donor);
  return (
    <section className="rounded-xl border border-gray-200 bg-white">
      <div className="flex gap-3 p-4">
        <PartPhoto c={c} size="lg" />
        <div className="min-w-0">
          <PartTitle c={c} className="text-[14px] font-semibold leading-snug" />
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span className="text-[17px] font-semibold text-gray-900">{PriceFormat(p.price)}</span>
            <Chip tone={stockStatusTone[p.status]}>{stockStatusLabel(p.status, t)}</Chip>
            <Chip>{p.condition}</Chip>
          </div>
          <p className="mt-1.5 text-[12px] text-gray-600">
            {t("Tag")} <span className="font-semibold text-gray-900">{p.tagNumber}</span> · {p.location}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-4 border-t border-gray-100 px-4 py-2.5 text-[13px]">
        {key && (
          <div className="col-span-2 mb-1.5 flex items-center justify-between gap-2">
            <span className="font-medium text-gray-700">{t(key.field)}</span>
            {key.value ? <ClickToCopy text={key.value} textClassName="font-mono text-[13px] font-semibold" /> : <Chip tone="amber">{t("Missing on donor")}</Chip>}
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          <span className="text-gray-500">OE</span>
          {p.oePartNumber ? (
            <ClickToCopy text={p.oePartNumber} textClassName="font-mono text-[12px]" />
          ) : key ? (
            // Engines and gearboxes go by their code, so a missing OE number isn't a problem.
            <span className="text-gray-400">—</span>
          ) : (
            <span className="flex items-center gap-1.5">
              <Chip tone="amber">{t("Missing")}</Chip>
              <span className="text-[12px] text-primary-700">{t("Add")}</span>
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-gray-500">{t("Part no.")}</span>
          {p.partNumber ? <ClickToCopy text={p.partNumber} textClassName="font-mono text-[12px]" /> : <span className="text-gray-400">—</span>}
        </div>
        {p.supersededPartNumbers && (
          <div className="col-span-2 mt-1 flex justify-between">
            <span className="text-gray-500">{t("Superseded")}</span>
            <span className="font-mono text-[12px]">{p.supersededPartNumbers}</span>
          </div>
        )}
      </div>
    </section>
  );
}

/** Part photo, linking to the part in Wilma when we know which part it is. */
function PartPhoto({ c, size }: { c: IInboxConversation; size: "sm" | "lg" }) {
  const { t } = useTranslation("messages");
  const p = c.part;
  if (!p) return null;
  const box = size === "lg" ? "h-24 w-24 rounded-lg" : "h-12 w-12 rounded-md";
  const inner = p.imageUrl ? (
    <img src={p.imageUrl} alt="" className={classNames("shrink-0 bg-gray-100 object-cover", box)} />
  ) : (
    <div className={classNames("flex shrink-0 flex-col items-center justify-center bg-gray-100 text-gray-400", box)}>
      <PhotoIcon className={size === "lg" ? "h-7 w-7" : "h-5 w-5"} />
      {size === "lg" && <span className="mt-1 text-[11px]">{p.photoCount === 1 ? t("{{count}} photo", { count: p.photoCount }) : t("{{count}} photos", { count: p.photoCount })}</span>}
    </div>
  );
  const href = partHref(c);
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" title={t("Open part in Wilma")} className="shrink-0 hover:opacity-80">
      {inner}
    </a>
  ) : (
    inner
  );
}

function PartTitle({ c, className }: { c: IInboxConversation; className: string }) {
  const { t } = useTranslation("messages");
  const href = partHref(c);
  if (!c.part) return null;
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" title={t("Open part in Wilma")} className={classNames("block text-gray-900 hover:text-primary-700 hover:underline", className)}>
      {c.part.title}
    </a>
  ) : (
    <p className={classNames("text-gray-900", className)}>{c.part.title}</p>
  );
}

function DonorCard({ c }: { c: IInboxConversation }) {
  const { t } = useTranslation("messages");
  const v = c.donor;
  if (!v) return null;
  return (
    <section className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4">
      <PlateChip reg={v.vrm} size="lg" />
      <div className="min-w-0 flex-1 text-[13px]">
        <p className="font-semibold text-gray-900">
          {t("Donor")} · {[v.make, v.model, v.variant, v.year].filter(Boolean).join(" ")}
        </p>
        <p className="text-[12px] text-gray-500">
          {[v.engineCode, v.engineSize, v.fuel, v.gearbox, v.colour].filter(Boolean).join(" · ")}
          {v.mileage ? ` · ${v.mileage.toLocaleString("en-GB")} mi` : ""}
          {v.vinLast8 ? ` · VIN …${v.vinLast8}` : ""}
        </p>
      </div>
      {donorHref(c) && (
        <a href={donorHref(c)} target="_blank" rel="noreferrer" className="shrink-0 text-[13px] font-medium text-primary-700 hover:underline">
          {t("View")} →
        </a>
      )}
    </section>
  );
}

function BuyerCard({ c }: { c: IInboxConversation }) {
  const { t } = useTranslation("messages");
  const b = c.buyer;
  // Order history isn't in the live API yet, so it only shows when we know it.
  const known = b.ordersWithUs !== undefined && b.spendWithUs !== undefined;
  const orders = b.ordersWithUs === 1 ? t("{{count}} order", { count: b.ordersWithUs }) : t("{{count}} orders", { count: b.ordersWithUs ?? 0 });
  const title = [t("Buyer"), b.username, ...(known ? [orders, PriceFormat(b.spendWithUs)] : [])].join(" · ");
  return (
    <PanelCard collapsible title={title}>
      <FieldRow label={t("Channel")}>{c.channel === "ebay" ? "eBay" : "PartPro"}</FieldRow>
      {b.fullName && <FieldRow label={t("Name")}>{b.fullName}</FieldRow>}
      {b.memberSince && <FieldRow label={t("Member since")}>{b.memberSince}</FieldRow>}
      {known && (
        <FieldRow label={t("Orders with us")}>
          {b.ordersWithUs} · {PriceFormat(b.spendWithUs)}
        </FieldRow>
      )}
      {b.otherOpenConversations !== undefined && <FieldRow label={t("Other open conversations")}>{b.otherOpenConversations || t("None")}</FieldRow>}
    </PanelCard>
  );
}

function Links({ c, reg }: { c: IInboxConversation; reg?: string }) {
  return (
    <div className="flex flex-wrap gap-2">
      {inboxLinks(c, reg).map((link) =>
        link.href ? (
          <a
            key={link.label}
            href={link.href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[13px] text-gray-700 hover:border-primary-300 hover:text-primary-700"
          >
            {link.label} <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
          </a>
        ) : (
          <span key={link.label} title={link.why} className="inline-flex cursor-not-allowed items-center gap-1 rounded-lg border border-dashed border-gray-200 bg-white px-2.5 py-1.5 text-[13px] text-gray-400">
            {link.label}
          </span>
        )
      )}
    </div>
  );
}

function PreSaleDetails({ c, reg, onReg, onCompare, onSendAnswer, onAddToReply }: DetailsProps) {
  const { t } = useTranslation("messages");
  const vehicleSpecificPart = !!c.donor;
  return (
    <>
      {vehicleSpecificPart && <FitmentCard conversation={c} reg={reg} onReg={onReg} onCompare={onCompare} onSendAnswer={onSendAnswer} onAddToReply={onAddToReply} />}
      {!c.part && !c.partId && (
        <p className="rounded-xl border border-dashed border-gray-300 bg-white p-4 text-[13px] text-gray-500">
          {c.itemTitle ? t("{{title}} isn't linked to a part in Wilma.", { title: c.itemTitle }) : t("This conversation isn't linked to a part in Wilma.")}
        </p>
      )}
      {c.orderNote && <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800">{c.orderNote}</p>}
      <PartCard c={c} />
      <DonorCard c={c} />
      {c.part && c.part.aspects.length > 0 && (
        <PanelCard collapsible title={`${t("Item specifics")} · ${c.part.aspects.length}`}>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[13px]">
            {c.part.aspects.map((a) => (
              <Fragment key={a.name}>
                <dt className="text-gray-500">{a.name}</dt>
                <dd className="text-gray-900">{a.value}</dd>
              </Fragment>
            ))}
          </dl>
        </PanelCard>
      )}
      <BuyerCard c={c} />
      <Links c={c} reg={reg ?? undefined} />
    </>
  );
}

function OrderMenu({ c, onAction }: { c: IInboxConversation; onAction: (kind: IOrderAction["kind"]) => void }) {
  const { t } = useTranslation("messages");
  const live = useInboxLive();
  const channelName = c.channel === "ebay" ? "eBay" : "PartPro";
  const blockFor = (kind: IOrderAction["kind"]) => (c.order ? orderActionBlock(c.order, kind) : undefined);
  const shipped = !!c.order && orderStage(c.order) === "with_courier";
  const actions: { kind: IOrderAction["kind"]; label: string; why?: string }[] = [
    { kind: "cancel", label: shipped ? t("Cancel order (already dispatched)") : t("Cancel order"), why: blockFor("cancel") },
    { kind: "refund_full", label: t("Refund in full…"), why: blockFor("refund_full") },
    { kind: "refund_partial", label: t("Partial refund…"), why: blockFor("refund_partial") },
  ];
  const orderLink = inboxLinks(c).find((l) => l.id === "order");
  // Stand-ins for what's still to come; on live data they're left out rather than shown greyed.
  const items = live ? [] : [
    t("View order in Wilma"),
    ...(orderLink?.href ? [] : [t("Open order on {{channel}}", { channel: channelName })]),
    t("Send invoice"),
    c.order?.invoiceNumber ? t("View invoice {{number}}", { number: c.order.invoiceNumber }) : t("View invoice"),
    t("Send return label"),
    t("Open ticket"),
  ];
  return (
    <Menu as="div" className="relative">
      <Menu.Button className="rounded-md border border-gray-200 p-1 text-gray-600 hover:bg-gray-50">
        <EllipsisVerticalIcon className="h-4 w-4" />
      </Menu.Button>
      <Transition as={Fragment} enter="transition ease-out duration-100" enterFrom="opacity-0" enterTo="opacity-100" leave="transition ease-in duration-75" leaveFrom="opacity-100" leaveTo="opacity-0">
        <Menu.Items className="absolute right-0 z-30 mt-1 w-56 rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
          {actions.map((a) => (
            <Menu.Item key={a.kind} disabled={!!a.why}>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => onAction(a.kind)}
                  disabled={!!a.why}
                  title={a.why}
                  className={classNames("block w-full px-3 py-1.5 text-left text-[13px]", a.why ? "text-gray-400" : "text-gray-800", active && "bg-gray-50")}
                >
                  {a.label}
                </button>
              )}
            </Menu.Item>
          ))}
          <div className="my-1 border-t border-gray-100" />
          {orderLink?.href && (
            <Menu.Item>
              {({ active }) => (
                <a href={orderLink.href} target="_blank" rel="noreferrer" className={classNames("flex items-center justify-between px-3 py-1.5 text-[13px] text-gray-800", active && "bg-gray-50")}>
                  {t("Open order on {{channel}}", { channel: channelName })} <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                </a>
              )}
            </Menu.Item>
          )}
          {items.map((label) => (
            <Menu.Item key={label} disabled>
              <span className="block px-3 py-1.5 text-[13px] text-gray-400" title={t("Not in this preview")}>
                {label}
              </span>
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

function WheresMyOrder({ c, onSendAnswer }: { c: IInboxConversation; onSendAnswer: (text: string) => void }) {
  const { t } = useTranslation("messages");
  const [includeAddress, setIncludeAddress] = useState(false);
  const o = c.order;
  if (!o) return null;
  const now = DateTime.utc();
  const lateDelivery = isLateDelivery(o, now);
  const trackingHref = trackingLink(o);
  // Live orders have no dispatch-by date yet, so they can't be late for dispatch.
  const dispatchBy = o.dispatchBy ? DateTime.fromISO(o.dispatchBy, { zone: "utc" }) : undefined;
  const late = !!dispatchBy && !o.shippedAt && dispatchBy < now;
  const lateShipped = !!dispatchBy && !!o.shippedAt && DateTime.fromISO(o.shippedAt, { zone: "utc" }) > dispatchBy;

  // "Label printed" stands in for Packed: Wilma has no packed step yet (plan §8, decision 6).
  const steps = [
    { label: "Paid", name: t("Paid"), who: c.channel === "ebay" ? "eBay" : "PartPro", at: o.orderedAt },
    { label: "Picked", name: t("Picked"), who: o.pickedBy, at: o.pickedAt },
    { label: "Label printed", name: t("Label printed"), who: o.labelPrintedBy, at: o.labelPrintedAt },
    { label: "Shipped", name: t("Shipped"), who: [o.shippedBy, o.courier].filter(Boolean).join(" · "), at: o.shippedAt },
    { label: "Delivered", name: t("Delivered"), who: t("estimated"), at: undefined as string | undefined, estimate: o.estimatedDelivery },
  ];
  const headline = o.shippedAt
    ? t("Shipped {{when}}", { when: when(o.shippedAt, { weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }) })
    : o.pickedAt
      ? t("Picked, not shipped yet")
      : t("Paid, not picked yet");

  return (
    <section className="rounded-xl border border-gray-200 bg-white">
      <div className="flex items-start justify-between px-4 pt-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{t("Where's my order")}</p>
          <p className="mt-0.5 flex items-center gap-1.5 text-[16px] font-semibold text-gray-900">
            <TruckIcon className="h-5 w-5 text-green-700" /> {headline}
          </p>
        </div>
        <Chip tone={late || lateShipped || lateDelivery ? "red" : "green"}>{late || lateShipped || lateDelivery ? t("Late") : t("On time")}</Chip>
      </div>
      {lateDelivery && (
        <p className="mx-4 mt-3 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-800">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          {daysPastEstimatedDelivery(o, now) === 1
            ? t("{{count}} day past its estimated delivery – look into it with {{courier}}.", { count: 1, courier: o.courier ?? t("the courier") })
            : t("{{count}} days past its estimated delivery – look into it with {{courier}}.", { count: daysPastEstimatedDelivery(o, now), courier: o.courier ?? t("the courier") })}
        </p>
      )}
      <ol className="px-4 py-3">
        {steps.map((step, i) => {
          const done = !!step.at;
          return (
            <li key={step.label} className="relative flex items-center gap-3 pb-3 last:pb-0">
              {i < steps.length - 1 && <span className={classNames("absolute left-[9px] top-5 h-full w-0.5", done ? "bg-green-600" : "bg-gray-200")} />}
              <span className={classNames("relative z-10 flex h-5 w-5 items-center justify-center rounded-full", done ? "bg-green-600" : "border-2 border-dashed border-gray-300 bg-white")}>
                {done && <CheckIcon className="h-3 w-3 text-white" />}
              </span>
              <span className="flex-1 text-[13px]">
                <span className={classNames("font-medium", done ? "text-gray-900" : "text-gray-500")}>{step.name}</span>
                {step.who && (done || step.label === "Delivered") && <span className="text-gray-500"> · {step.who}</span>}
              </span>
              <span className="text-[12px] text-gray-500">{step.at ? when(step.at) : step.estimate ? when(step.estimate, INBOX_DAY) : ""}</span>
            </li>
          );
        })}
      </ol>
      <div className="grid grid-cols-2 gap-3 rounded-b-xl bg-gray-50 px-4 py-3 text-[13px]">
        <div>
          <p className="text-[12px] text-gray-500">{t("Dispatch by")}</p>
          <p className="font-medium text-gray-900">
            {o.dispatchBy ? when(o.dispatchBy, INBOX_DAY) : "—"}{" "}
            {o.shippedAt && dispatchBy && <span className={lateShipped ? "text-red-700" : "text-green-700"}>{lateShipped ? t("missed") : t("met")}</span>}
          </p>
        </div>
        <div>
          <p className="text-[12px] text-gray-500">{t("Estimated delivery")}</p>
          <p className="font-medium text-gray-900">
            {o.estimatedDeliveryFrom && o.estimatedDeliveryTo && when(o.estimatedDeliveryFrom, INBOX_DAY) !== when(o.estimatedDeliveryTo, INBOX_DAY)
              ? `${when(o.estimatedDeliveryFrom, INBOX_DAY)} – ${when(o.estimatedDeliveryTo, INBOX_DAY)}`
              : o.estimatedDelivery
                ? when(o.estimatedDelivery, INBOX_DAY)
                : "—"}
          </p>
        </div>
        <div>
          <p className="text-[12px] text-gray-500">{t("Courier · service")}</p>
          <p className="font-medium text-gray-900">{[o.courier, o.service].filter(Boolean).join(" · ") || "—"}</p>
        </div>
        <div>
          <p className="text-[12px] text-gray-500">{t("Tracking")}</p>
          {o.trackingNumber ? (
            trackingHref ? (
              <a href={trackingHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono text-[12px] font-medium text-primary-700 hover:underline">
                {o.trackingNumber} <ArrowTopRightOnSquareIcon className="h-3 w-3" />
              </a>
            ) : (
              <ClickToCopy text={o.trackingNumber} textClassName="font-mono text-[12px]" />
            )
          ) : (
            <p className="text-gray-400">{t("Not yet")}</p>
          )}
        </div>
      </div>
    <div className="flex items-center gap-3 border-t border-gray-100 px-4 py-3">
        <button type="button" onClick={() => onSendAnswer(wheresMyOrderReply(c, now, includeAddress))} className="rounded-lg bg-primary-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-primary-700">
          {t("Draft order update")}
        </button>
        {!o.isCollection && (
          <label className="flex items-center gap-1.5 text-[12px] text-gray-600">
            <input type="checkbox" checked={includeAddress} onChange={(e) => setIncludeAddress(e.target.checked)} className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
            {t("Include delivery address")}
          </label>
        )}
      </div>
    </section>
  );
}

function changeTitle(type: IInboxConversation["type"], t: (key: string) => string) {
  if (type === "cancellation") return t("Cancellation request");
  if (type === "address_change") return t("Address change");
  if (type === "amendment") return t("Order change");
  return undefined;
}

const adviceTone = {
  green: "border-green-200 bg-green-50 text-green-900",
  amber: "border-amber-200 bg-amber-50 text-amber-900",
  red: "border-red-200 bg-red-50 text-red-900",
};

/** Cancellations, address changes and order changes: what's possible at this stage, and the drafts. */
function OrderChangeCard({ c, onSendAnswer, onWarehouseAlert, onAction }: Pick<DetailsProps, "c" | "onSendAnswer" | "onWarehouseAlert"> & { onAction: (kind: IOrderAction["kind"]) => void }) {
  const { t } = useTranslation("messages");
  // Warehouse alerts need a #warehouse channel Wilma can post to, so they're sample data only.
  const canAlert = !useInboxLive();
  const [alerting, setAlerting] = useState(false);
  const now = DateTime.utc();
  const advice: IOrderChangeAdvice | undefined =
    c.type === "cancellation" ? cancellationAdvice(c, now) : c.type === "address_change" ? addressChangeAdvice(c, now) : c.type === "amendment" ? amendmentAdvice(c, now) : undefined;
  if (!advice || !c.order) return null;
  const shipped = orderStage(c.order) === "with_courier";

  return (
    <section className={classNames("rounded-xl border p-4", adviceTone[advice.tone])}>
      <p className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{changeTitle(c.type, t)} · {t("urgent")}</p>
      <p className="mt-0.5 text-[15px] font-semibold">{advice.headline}</p>
      {c.ebayCancelRequest && (
        <p className="mt-1 text-[12px]">
          {t("Opened through eBay ({{reason}}) – answer it in eBay's cancellation request, not just by message.", { reason: c.ebayCancelRequest.reason })}
        </p>
      )}
      <ol className="mt-2 list-decimal space-y-0.5 pl-5 text-[13px]">
        {advice.steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <div className="mt-3 flex flex-wrap gap-2">
        {canAlert && advice.alertFirst && advice.warehouseNote && (
          <button type="button" onClick={() => setAlerting(true)} className="rounded-lg bg-red-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-red-700">
            Alert warehouse
          </button>
        )}
        <button
          type="button"
          onClick={() => onSendAnswer(advice.reply)}
          className={classNames(
            "rounded-lg px-3 py-1.5 text-[13px] font-medium",
            canAlert && advice.alertFirst ? "border border-gray-300 bg-white text-gray-800 hover:bg-gray-50" : "bg-primary-600 text-white hover:bg-primary-700"
          )}
        >
          {t("Draft reply")}
        </button>
        {canAlert && !advice.alertFirst && advice.warehouseNote && (
          <button type="button" onClick={() => setAlerting(true)} className="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-[13px] font-medium text-red-700 hover:bg-red-50">
            Alert warehouse
          </button>
        )}
        {c.type === "cancellation" && !orderActionBlock(c.order, "cancel") && (
          <button type="button" onClick={() => onAction("cancel")} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-[13px] font-medium text-gray-800 hover:bg-gray-50">
            {t("Cancel order…")}
          </button>
        )}
        {c.type === "address_change" && !shipped && (
          <button
            type="button"
            disabled
            title={t("Will open the part's Manage shipping, where the address is edited – not in this preview")}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-[13px] text-gray-400"
          >
            {t("Open Manage shipping")}
          </button>
        )}
      </div>
      {canAlert && alerting && advice.warehouseNote && (
        <WarehouseAlertPanel
          text={advice.warehouseNote}
          onClose={() => setAlerting(false)}
          onSend={(text) => {
            onWarehouseAlert(text);
            setAlerting(false);
          }}
        />
      )}
      {c.type === "address_change" && c.channel === "ebay" && !shipped && (
        <p className="mt-2 text-[12px] opacity-80">
          {t("This changes the address in Wilma and on the label only – the order on eBay keeps the old one. eBay's seller protection only covers the address on the order, so check with Adam before sending elsewhere.")}
        </p>
      )}
    </section>
  );
}

function PostSaleDetails({ c, onSendAnswer, onWarehouseAlert, onAction }: Pick<DetailsProps, "c" | "onSendAnswer" | "onWarehouseAlert"> & { onAction: (kind: IOrderAction["kind"]) => void }) {
  const { t } = useTranslation("messages");
  const live = useInboxLive();
  const o = c.order;
  if (!o) return null;
  const cancelBlock = orderActionBlock(o, "cancel");
  return (
    <>
      <OrderChangeCard c={c} onSendAnswer={onSendAnswer} onWarehouseAlert={onWarehouseAlert} onAction={onAction} />

      {c.part && (
        <section className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
          <PartPhoto c={c} size="sm" />
          <div className="min-w-0">
            <PartTitle c={c} className="truncate text-[13px] font-semibold" />
            <p className="text-[12px] text-gray-500">
              {t("Tag no.")} {c.part.tagNumber} · {PriceFormat(c.part.price)} · {c.part.location}
            </p>
          </div>
        </section>
      )}

      <WheresMyOrder c={c} onSendAnswer={onSendAnswer} />

      <PanelCard title={t("Order")} right={<OrderMenu c={c} onAction={onAction} />}>
        <FieldRow label={t("Order no.")}>
          <ClickToCopy text={o.orderNumber} textClassName="font-mono text-[12px]" />
        </FieldRow>
        <FieldRow label={t("Channel")}>{c.channel === "ebay" ? "eBay" : "PartPro"}</FieldRow>
        <FieldRow label={t("Order date")}>{when(o.orderedAt)}</FieldRow>
        <FieldRow label={t("Price paid")}>
          {t("{{price}} + {{postage}} postage", { price: PriceFormat(o.pricePaid), postage: PriceFormat(o.postage) })}
        </FieldRow>
        <FieldRow label={t("Payment")}>
          <Chip tone="green">{o.paymentStatus}</Chip>
        </FieldRow>
        <FieldRow label={t("Invoice")}>{o.invoiceNumber ?? "—"}</FieldRow>
      </PanelCard>

      <PanelCard title={t("Delivery")}>
        {o.isCollection ? (
          <p className="text-[13px] font-medium text-gray-900">{t("Collection")}</p>
        ) : (
          <div className="flex items-start justify-between gap-2 text-[13px] text-gray-900">
            <div>
              <p className="font-semibold">{o.deliveryName}</p>
              {o.deliveryAddress?.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
            <ClickToCopy text="" value={[o.deliveryName, ...(o.deliveryAddress ?? [])].join("\n")} title={t("Copy address")} />
          </div>
        )}
        <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-2 text-[13px]">
          <span className="text-gray-500">{t("Buyer's car reg")}</span>
          {o.buyerVehicleReg ? (
            <PlateChip reg={o.buyerVehicleReg} />
          ) : (
            <span className="text-gray-500">
              {t("Not given")} · <span className="font-medium text-primary-700">{t("Ask buyer")}</span>
            </span>
          )}
        </div>
      </PanelCard>

      <div className="grid grid-cols-2 gap-2">
        {(live ? [] : [t("Add / change tracking"), t("Start return"), t("Add note")]).map((label) => (
          <button key={label} type="button" disabled title={t("Not in this preview")} className="rounded-lg border border-gray-300 bg-white py-2 text-[13px] text-gray-500">
            {label}
          </button>
        ))}
        {live &&
          (["refund_full", "refund_partial"] as const).map((kind) => {
            const why = orderActionBlock(o, kind);
            return (
              <button
                key={kind}
                type="button"
                onClick={() => onAction(kind)}
                disabled={!!why}
                title={why}
                className="rounded-lg border border-gray-300 bg-white py-2 text-[13px] font-medium text-gray-800 hover:bg-gray-50 disabled:font-normal disabled:text-gray-400"
              >
                {kind === "refund_full" ? t("Refund in full…") : t("Partial refund…")}
              </button>
            );
          })}
        <button
          type="button"
          onClick={() => onAction("cancel")}
          disabled={!!cancelBlock}
          title={cancelBlock}
          className="rounded-lg border border-red-300 bg-white py-2 text-[13px] font-medium text-red-700 hover:bg-red-50 disabled:border-red-200 disabled:font-normal disabled:text-red-300"
        >
          {t("Cancel order…")}
        </button>
      </div>

      <BuyerCard c={c} />
      <Links c={c} />
    </>
  );
}

function ReturnTab({ c, onAction, onSendAnswer }: { c: IInboxConversation; onAction: (kind: IOrderAction["kind"]) => void; onSendAnswer: (text: string) => void }) {
  const { t } = useTranslation("messages");
  const r = c.returnCase;
  if (c.ebayCase) return <EbayCaseCard c={c} onAction={onAction} onSendAnswer={onSendAnswer} />;
  if (!r) return <p className="p-4 text-[13px] text-gray-500">{t("No return or eBay case on this conversation.")}</p>;
  const respondBy = DateTime.fromISO(r.respondBy, { zone: "utc" });
  const left = respondBy.diff(DateTime.utc(), ["days", "hours"]);
  const leftText = left.toMillis() > 0 ? t("{{days}}d {{hours}}h", { days: Math.floor(left.days), hours: Math.floor(left.hours) }) : t("Overdue");
  const urgent = left.as("hours") < 24;

  return (
    <div className="space-y-3">
      {c.part && (
        <section className="rounded-xl border border-gray-200 bg-white p-3 text-[13px]">
          <p className="font-semibold text-gray-900">{c.part.title}</p>
          <p className="text-[12px] text-gray-500">
            {t("Tag no.")} {c.part.tagNumber} · {PriceFormat(c.part.price)} · {t("Order")} {c.order?.orderNumber}
          </p>
        </section>
      )}
      <section className="overflow-hidden rounded-xl border border-amber-300 bg-white">
        <div className="bg-amber-50 px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">{t("eBay return request")}</p>
          <p className="text-[16px] font-semibold text-gray-900">{r.reason}</p>
          <p className="text-[13px] text-amber-800">{r.state}</p>
        </div>
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <p className="text-[12px] text-gray-500">{t("Respond by")}</p>
            <p className="text-[13px] font-medium text-gray-900">{when(r.respondBy)} · {t("eBay deadline")}</p>
          </div>
          <p className={classNames("text-[24px] font-bold", urgent ? "text-red-700" : "text-amber-700")}>{leftText}</p>
        </div>
        <div className="space-y-1 border-t border-gray-100 px-4 py-3">
          <FieldRow label={t("Return ID")}>{r.returnId}</FieldRow>
          <FieldRow label={t("Opened")}>{when(r.openedAt)}</FieldRow>
          <FieldRow label={t("Requested")}>{r.requested}</FieldRow>
          <p className="pt-1 text-[13px] italic text-gray-700">"{r.buyerReason}"</p>
          <div className="flex gap-2 pt-2">
            {Array.from({ length: r.photoCount }).map((_, i) => (
              <div key={i} className="flex h-14 w-14 items-center justify-center rounded-md bg-gray-100 text-gray-400">
                <PhotoIcon className="h-5 w-5" />
              </div>
            ))}
          </div>
        </div>
        <div className="border-t border-gray-100 px-4 py-3">
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{t("Policy check")}</p>
          <ul className="space-y-1.5">
            {r.policyChecks.map((check) => (
              <li key={check.text} className="flex items-start gap-2 text-[13px] text-gray-800">
                <ResultIcon result={check.result} className="mt-0.5 h-4 w-4 shrink-0" />
                {check.text}
              </li>
            ))}
          </ul>
        </div>
        <div className="grid grid-cols-3 gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" onClick={() => onAction("refund_partial")} className="rounded-lg border border-gray-300 py-1.5 text-[12px] font-medium text-gray-800 hover:bg-gray-50">
            {t("Partial refund…")}
          </button>
          {[t("Accept return"), t("Decline"), t("Mark received"), t("Refund sent")].map((label) => (
            <button key={label} type="button" disabled title={t("Comes with the returns work")} className="rounded-lg border border-gray-300 py-1.5 text-[12px] text-gray-500">
              {label}
            </button>
          ))}
        </div>
        <PreviewNote>
          <span className="px-4">{t("Accept, decline and mark received come with the returns work, which this tab will link to.")}</span>
        </PreviewNote>
        <div className="h-3" />
      </section>
    </div>
  );
}

function NotesTab({ c, usersById }: { c: IInboxConversation; usersById: Record<string, IInboxUser> }) {
  const { t } = useTranslation("messages");
  const notes = c.messages.filter((m) => m.direction === "note");
  if (notes.length === 0) return <p className="p-4 text-[13px] text-gray-500">{t('No internal notes yet. Switch the reply box to "Note" to add one.')}</p>;
  return (
    <ul className="space-y-2">
      {notes.map((n) => {
        const author = n.authorId ? usersById[n.authorId] : undefined;
        return (
          <li key={n.id} className="rounded-xl border border-violet-200 bg-violet-50 p-3 text-[13px]">
            <p className="text-gray-900">{n.body}</p>
            <p className="mt-1 text-[11px] text-gray-500">
              {author ? `${author.forename} ${author.surname}` : t("You")} · {when(n.sentAt)}
            </p>
          </li>
        );
      })}
    </ul>
  );
}

interface DetailsProps {
  c: IInboxConversation;
  reg: string | null;
  onReg: (reg: string) => void;
  onCompare: (reg: string) => void;
  onSendAnswer: (text: string) => void;
  onAddToReply: (text: string) => void;
  /** Urgent alert to the #warehouse Team Chat channel (pushes to their phones). */
  onWarehouseAlert: (text: string) => void;
  /** Live data: resolves with what happened, and the panel stays open to show it. */
  onOrderAction: (action: IOrderAction) => Promise<IInboxNotice | undefined> | void;
  fillMerge: (text: string) => string;
}

interface OrderNoticeProps {
  /** Live data: what the last cancel or refund on this conversation came back with, kept by the page so it survives the panel. */
  orderNotice?: IInboxNotice;
  /** Clears it, when a new cancel or refund starts. */
  onClearOrderNotice: () => void;
}

interface ContextPanelProps extends DetailsProps, OrderNoticeProps {
  tab: PanelTab;
  onTab: (tab: PanelTab) => void;
  onHide: () => void;
  usersById: Record<string, IInboxUser>;
  /** Live data: the part context (part, donor, order) is fetched when the conversation opens. */
  contextStatus?: { loading: boolean; error?: string; onRetry: () => void };
}

export default function ContextPanel({ c, tab, onTab, onHide, usersById, contextStatus, orderNotice, onClearOrderNotice, ...details }: ContextPanelProps) {
  const { t } = useTranslation("messages");
  const live = useInboxLive();
  // Cancel / refund / partial refund being confirmed; opened from the order menu, an urgent card or a case.
  const [pendingAction, setPendingAction] = useState<IOrderAction["kind"]>();
  useEffect(() => setPendingAction(undefined), [c.id]);
  const openAction = (kind: IOrderAction["kind"]) => {
    onClearOrderNotice();
    setPendingAction(kind);
  };
  const actionPanel = pendingAction && (
    <OrderActionPanel
      key={pendingAction}
      c={c}
      kind={pendingAction}
      fillMerge={details.fillMerge}
      notice={orderNotice}
      onClose={() => setPendingAction(undefined)}
      onConfirm={(action) => {
        // Live: the panel stays open with the result. Sample: it just closes.
        if (live) return details.onOrderAction(action);
        details.onOrderAction(action);
        setPendingAction(undefined);
      }}
    />
  );
  const noteCount = c.messages.filter((m) => m.direction === "note").length;
  // Notes, returns and eBay cases have no live data yet, so live shows the details only.
  const tabs: { id: PanelTab; label: ReactNode }[] = [
    { id: "details", label: t("Details") },
    ...(live
      ? []
      : [
          { id: "notes" as const, label: <>{t("Notes")} {noteCount > 0 && <span className="ml-1 rounded-full bg-gray-100 px-1.5 text-[11px] text-gray-600">{noteCount}</span>}</> },
          { id: "return" as const, label: <>{c.ebayCase ? t("Case") : t("Return")} {(c.returnCase || c.ebayCase) && <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-red-600 align-middle" />}</> },
        ]),
  ];
  const shownTab = live ? "details" : tab;

  return (
    <aside className="flex w-[392px] shrink-0 flex-col border-l border-gray-200 bg-gray-50">
      <div className="flex h-11 shrink-0 items-stretch gap-4 border-b border-gray-200 bg-white px-4">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            type="button"
            onClick={() => onTab(tb.id)}
            className={classNames("flex items-center border-b-2 text-[13px] font-medium", shownTab === tb.id ? "border-primary-600 text-primary-700" : "border-transparent text-gray-600 hover:text-gray-900")}
          >
            {tb.label}
          </button>
        ))}
        <button type="button" onClick={onHide} title={t("Hide panel")} className="ml-auto self-center rounded p-1 text-gray-500 hover:bg-gray-100">
          <ViewColumnsIcon className="h-4 w-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {shownTab !== "notes" && actionPanel}
        {/* The result outlives the panel: it's still here after moving away and coming back. */}
        {shownTab === "details" && !pendingAction && orderNotice && <InboxNotice notice={orderNotice} />}
        {shownTab === "details" && contextStatus?.error && <InboxError text={contextStatus.error} onRetry={contextStatus.onRetry} />}
        {shownTab === "details" && contextStatus?.loading && !c.part && <InboxLoading text={t("Fetching the part and order…")} />}
        {shownTab === "details" && (c.order ? <PostSaleDetails c={c} {...details} onAction={openAction} /> : <PreSaleDetails c={c} {...details} />)}
        {shownTab === "notes" && <NotesTab c={c} usersById={usersById} />}
        {shownTab === "return" && <ReturnTab c={c} onAction={openAction} onSendAnswer={details.onSendAnswer} />}
      </div>
    </aside>
  );
}
